// @givmo/app-charity — the popup's tiles, from the ONE square derivation (shell contract v3).
//
// `useConsoleSquares` decides WHICH squares exist, in what STATE and WHERE they go; this maps each
// to the design system's `ModuleSquareProps` for the nine-dot popup's two sections (D6): "My Givmo
// Apps" — the squares this person can OPEN (the Admin Console tile last, badged) — and "More Givmo
// apps" — every other app offered to the organization's class, each with its ONE status line and
// its request or "Request declined" link.
//
// What the host decides here, and the package deliberately does not:
//
//   * THE REQUEST DOOR IS WITHHELD ON A MODULE THE PERSON ALREADY HOLDS. An OFF app they hold is a
//     statement (or "Turn on"), never "Request access" — the server refuses a holder 409
//     `ALREADY_HAS_ACCESS`, and a door onto a refusal is the dead control this shell bans.
//   * "TURN ON" GOES ONLY TO A HOLDER OF `write_settings` — the switch's own capability, read off
//     the standing read. Never a tier (the build law).
//   * AN ANNOUNCED MODULE THIS ORGANIZATION IS NOT OFFERED is a "Coming soon" square (OM-33) under
//     the line — no link, no door, no lock. There is no tile options menu (RULED 2026-10-01 07:02
//     PDT): the default landing page is each person's own setting, on the settings page.

import { Icon, type ModuleSquareProps } from '@givmo/design-system';
import { moduleTurnOnHref, type ConsoleSquare, type ModuleStandings } from '@givmo/org-console';
import type { CharityTableRow } from './module-table';

export interface TileContext {
  /** The caller holds `write_settings` — the Turn On door's capability (never a tier check). */
  canTurnOn: boolean;
  /** Hook modules whose module-owned activation transport this host actually mounted. */
  activationHandlers: readonly string[];
  /** Where "Turn on" and "Setting up" go for a holder — the Admin Console's Apps page. */
  managementHref: string;
  /** The caller's per-module standing, for the held/unheld split of the request door. */
  standings: ModuleStandings | null;
  /** Open the request dialog for a module this person does not hold. */
  onRequestAccess: (module: string) => void;
}

export interface ConsoleTiles {
  mine: ModuleSquareProps[];
  more: ModuleSquareProps[];
}

function tileFor(square: ConsoleSquare<CharityTableRow>, context: TileContext): ModuleSquareProps {
  const { row } = square;
  const icon = <Icon icon={row.icon} size={22} aria-hidden />;
  const base = { slug: row.slug, label: row.label, icon };

  if (square.verdict === 'link' && square.destination !== null) {
    const inApp = square.destination.kind === 'in-app';
    if (square.kind === 'admin-console') {
      return {
        ...base,
        href: square.destination.href,
        inApp,
        current: square.current,
        adminConsole: true,
        ...(square.badge === undefined ? {} : { badge: square.badge }),
      };
    }
    return { ...base, href: square.destination.href, inApp, current: square.current };
  }

  // OM-33: announced to the class, not offered to this organization — a statement, nothing more.
  if (square.verdict === 'coming-soon') return { ...base, state: 'coming-soon' };

  const accessRequest =
    square.accessRequest?.status === 'PENDING'
      ? { status: 'pending' as const }
      : square.accessRequest?.status === 'DENIED'
        ? { status: 'declined' as const, note: square.accessRequest.resolutionNote ?? null }
        : undefined;
  // The door is for a person who does NOT hold the app — see the header.
  const held = context.standings?.[row.slug]?.held === true;
  const door = held
    ? {}
    : {
        onRequestAccess: () => context.onRequestAccess(row.slug),
        ...(accessRequest === undefined ? {} : { accessRequest }),
      };

  if (square.verdict === 'locked') return { ...base, state: 'locked', ...door };

  const turnOnHref = context.canTurnOn
    ? moduleTurnOnHref(row.slug, context.managementHref, context.activationHandlers)
    : null;
  if (square.verdict === 'off') {
    return turnOnHref === null
      ? { ...base, state: 'off', ...door }
      : { ...base, state: 'off', turnOn: true, href: turnOnHref };
  }
  // `setting-up`: a real management link for someone who may manage setup, a statement otherwise.
  return context.canTurnOn
    ? { ...base, state: 'setting-up', viewSetup: true, href: context.managementHref }
    : { ...base, state: 'setting-up' };
}

/** The popup's two sections over the derived squares. */
export function squaresToTiles(
  squares: readonly ConsoleSquare<CharityTableRow>[],
  context: TileContext,
): ConsoleTiles {
  const mine: ModuleSquareProps[] = [];
  const more: ModuleSquareProps[] = [];
  for (const square of squares) {
    (square.verdict === 'link' ? mine : more).push(tileFor(square, context));
  }
  return { mine, more };
}
